import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../services/auth.service';
import { TokenService } from '../../services/token.service';
import { WebsocketService } from '../../services/websocket.service';
import { InputSubmitComponent } from '../../shared/components/input-submit/input-submit.component';
import { InputTextComponent } from '../../shared/components/input-text/input-text.component';
import { TranslateSafePipe } from '../../shared/pipes/translation.pipe';
import { AzureLoginResponseDto, LoginResponseDto } from '../../types/dtos/auth.dto';
import { BaseResponseDto } from '../../types/dtos/base.dto';
import { InputSubmitInterface, InputTextInterface } from '../../types/interfaces/input.interface';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    InputTextComponent,
    InputSubmitComponent,
    TranslateSafePipe,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit, AfterViewInit {
  public currentYear: number = new Date().getFullYear();
  public loginForm: FormGroup = new FormGroup({
    username: new FormControl('', [
      Validators.required,
    ]),
    password: new FormControl('', [
      Validators.required,
    ]),
  });
  public usernameConfig: InputTextInterface = {
    formGroup: this.loginForm,
    isTranslated: true,
    label: 'common.fields.username',
    id: 'username',
    type: 'text',
    iconLeft: 'person',
  };
  public passwordConfig: InputTextInterface = {
    formGroup: this.loginForm,
    isTranslated: true,
    label: 'common.fields.password',
    id: 'password',
    type: 'password',
    iconLeft: 'lock',
    visibility: true,
  };
  public submitConfig: InputSubmitInterface = {
    formGroup: this.loginForm,
    isTranslated: true,
    label: 'common.action.login',
  };

  public azureLoginLink: string | undefined;

  constructor(
    private readonly _authService: AuthService,
    private readonly _tokenService: TokenService,
    private readonly _messageService: MessageService,
    private readonly _router: Router,
    private readonly _websocketService: WebsocketService,
    private readonly _translateService: TranslateService,
    private readonly _route: ActivatedRoute,
  ) {
  }

  public ngOnInit(): void {


    this._requestAzureLoginLink();
  }

  public ngAfterViewInit(): void {
    this._route.queryParams.subscribe((params: Params) => {
      let code: string | undefined = params['code'];

      if (code) {
        this._loginAzure(code);
      }
    });
  }

  public doLogin(): void {
    const {username, password} = this.loginForm.value;

    if (username && password) {
      this._authService.login({username, password})
        .subscribe({
          next: (result: LoginResponseDto) => {
            this._tokenService.setAccessToken(result.access_token!);
            this._tokenService.setRefreshToken(result.refresh_token!);

            this._router.navigate(['/'])
              .then(() => {
                this._authService.isLogin();
                this._websocketService.connect();
              });
          },
          error: () => {
            this._messageService.add({
              severity: 'error',
              summary: this._translateService.instant('common.title.login-fail'),
              detail: this._translateService.instant('common.messages.error.login-fail'),
            });
          },
        });
    }
  }

  private _requestAzureLoginLink(): void {
    this._authService.getAzureLoginLink()
      .subscribe({
        next: (result: BaseResponseDto<AzureLoginResponseDto>) => {
          this.azureLoginLink = result.data.loginLink;
        },
      });
  }

  private _loginAzure(code: string): void {
    this._authService.loginAzure({
      code,
    })
      .subscribe({
        next: (result: LoginResponseDto) => {
          if (result.access_token && result.refresh_token) {
            this._tokenService.setAccessToken(result.access_token!);
            this._tokenService.setRefreshToken(result.refresh_token!);

            this._router.navigate(['/'])
              .then(() => {
                this._authService.isLogin();
                this._websocketService.connect();
              });
          }
        },
        error: () => {
          this._messageService.add({
            severity: 'error',
            summary: this._translateService.instant('common.title.login-fail'),
          });
        },
      });
  }
}
